import { useCallback, useMemo, useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import moment from 'moment';

export const useLeadsFilter = (columns, searchQuery, selectedCadenceData, selectedMonth) => {
  var debouncedSearchQueryState = useState(searchQuery);
  var debouncedSearchQuery = debouncedSearchQueryState[0];
  var setDebouncedSearchQuery = debouncedSearchQueryState[1];

  // Debounce the search query
  useEffect(function() {
    var debounced = debounce(function() {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    debounced();
    return function() { debounced.cancel(); };
  }, [searchQuery]);

  // Memoize the searchable fields to avoid recreating them on every render
  var getSearchableFields = useCallback(function(lead) {
    if (!lead) return [];
    return [
      lead.name,
      lead.email,
      lead.phone,
      lead.company,
      get(lead, 'additional_data.instagram', ''),
      get(lead, 'additional_data.nextPendingTask.name', '')
    ].filter(Boolean);
  }, []);

  // Helper function to check if lead matches cadence
  var leadMatchesCadence = useCallback(function(lead, cadenceName) {
    // If lead has no cadence data, include it (will go to "Inicio" column)
    if (!lead.additional_data || !lead.additional_data.cadences) {
      return true;
    }

    return lead.additional_data.cadences.some(function(cadence) {
      return cadence.name === cadenceName;
    });
  }, []);

  // Helper function to check if lead matches date (supports daily and monthly formats)
  var leadMatchesDate = useCallback(function(lead, selectedDate) {
    if (!selectedDate) return true;

    // Use column_updated_at for date filtering (when lead was moved to current column)
    var dateToCompare = lead.column_updated_at || lead.updated_at || lead.created_at;
    var leadMoment = moment(dateToCompare);

    // Check if selectedDate is a daily format (YYYY-MM-DD) or monthly (YYYY-MM)
    if (selectedDate.length === 10) {
      // Daily format - compare exact date
      return leadMoment.format('YYYY-MM-DD') === selectedDate;
    } else {
      // Monthly format - compare month and year
      return leadMoment.format('YYYY-MM') === selectedDate;
    }
  }, []);

  // Helper function to check if lead matches search query
  var leadMatchesSearch = useCallback(function(lead, query) {
    if (!query) return true;
    if (!lead) return false;

    var searchableFields = getSearchableFields(lead);
    return searchableFields.some(function(value) {
      return value && value.toString().toLowerCase().indexOf(query) !== -1;
    });
  }, [getSearchableFields]);

  // Column IDs that need date filtering
  var COLUMN_IDS_WITH_DATE_FILTER = useMemo(function() {
    return { 5: true, 6: true }; // Positivo and Negativo - using object for O(1) lookup
  }, []);

  // Single-pass filtering: filter leads and group by column in one operation
  // This replaces the previous triple filtering approach
  var filterResult = useMemo(function() {
    if (!columns || columns.length === 0) {
      return { filteredData: [], leadsByColumn: {} };
    }

    var query = debouncedSearchQuery ? debouncedSearchQuery.toLowerCase().trim() : null;
    var cadenceName = selectedCadenceData && selectedCadenceData.name;

    var allFiltered = [];
    var byColumn = {};

    // Initialize column buckets
    columns.forEach(function(column) {
      byColumn[column.id] = [];
    });

    // Single pass through all columns and their leads
    columns.forEach(function(column) {
      var columnLeads = column.data || [];

      columnLeads.forEach(function(lead) {
        // Skip if doesn't match search
        if (query && !leadMatchesSearch(lead, query)) {
          return;
        }

        // Skip if doesn't match cadence
        if (cadenceName && !leadMatchesCadence(lead, cadenceName)) {
          return;
        }

        // Lead passed all filters - add to results
        var leadWithColumn = Object.assign({}, lead, { column_id: column.id });
        allFiltered.push(leadWithColumn);

        // Add to column bucket
        if (byColumn[column.id]) {
          byColumn[column.id].push(leadWithColumn);
        }
      });
    });

    return { filteredData: allFiltered, leadsByColumn: byColumn };
  }, [columns, debouncedSearchQuery, selectedCadenceData, leadMatchesSearch, leadMatchesCadence]);

  // Build the final columns structure with date filtering for Positivo/Negativo
  var filteredDataToColumns = useMemo(function() {
    if (!columns || columns.length === 0) return [];

    var leadsByColumn = filterResult.leadsByColumn;
    var result = [];
    var hasAddedMergedLeadsColumn = false;

    columns.forEach(function(column) {
      // Skip column 2 - we merge it with column 1
      if (column.id === 2) {
        return;
      }

      // Merge columns 1 and 2 into a single "Leads" column
      if (column.id === 1) {
        if (!hasAddedMergedLeadsColumn) {
          // Get leads from both column 1 (Inicio) and column 2 (Leads)
          var inicioLeads = leadsByColumn[1] || [];
          var leadsLeads = leadsByColumn[2] || [];
          var mergedLeads = inicioLeads.concat(leadsLeads);

          result.push({
            id: column.id,
            name: 'Leads',
            style: column.style,
            type: column.type,
            data: mergedLeads,
            total: mergedLeads.length
          });
          hasAddedMergedLeadsColumn = true;
        }
        return;
      }

      // Get leads for this column
      var columnLeads = leadsByColumn[column.id] || [];

      // Apply date filter only to Positivo (5) and Negativo (6) columns
      if (COLUMN_IDS_WITH_DATE_FILTER[column.id] && selectedMonth) {
        columnLeads = columnLeads.filter(function(lead) {
          return leadMatchesDate(lead, selectedMonth);
        });
      }

      result.push({
        id: column.id,
        name: column.name,
        style: column.style,
        type: column.type,
        stepData: column.stepData,
        data: columnLeads,
        total: columnLeads.length
      });
    });

    return result;
  }, [columns, filterResult, selectedMonth, leadMatchesDate, COLUMN_IDS_WITH_DATE_FILTER]);

  return {
    filteredData: filterResult.filteredData,
    filteredDataToColumns: filteredDataToColumns
  };
};
